/**
 * SDK Slice
 * Manages SDK settings and auction parameters
 */

import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { storefrontAPI } from '../../utils/api';
import { AuctionParams, SDKSettings } from '../types';

// State type
export interface SDKState {
    auctionParams: AuctionParams | null;
    isLoading: boolean;
    isLaunching: boolean; // True when SDK launch is in progress
    error: string | null;
}

// Initial State
export const sdkInitialState: SDKState = {
    auctionParams: null,
    isLoading: false,
    isLaunching: false,
    error: null,
};

// Async Thunks

/**
 * Get SDK settings for widget initialization
 */
export const getSDKSettings = createAsyncThunk<SDKSettings, void, { rejectValue: string }>(
    'sdk/getSDKSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.getSDKSettings();
            if (response.error) {
                return rejectWithValue(response.error);
            }
            return response;
        } catch {
            return rejectWithValue('Failed to get SDK settings');
        }
    },
);

// Slice
export const sdkSlice = createSlice({
    name: 'sdk',
    initialState: sdkInitialState,
    reducers: {
        setAuctionParams(state, action: PayloadAction<AuctionParams>) {
            state.auctionParams = action.payload;
        },
        resetAuctionParams(state) {
            state.auctionParams = null;
        },
        setSDKLaunching(state, action: PayloadAction<boolean>) {
            state.isLaunching = action.payload;
        },
        setSDKError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        resetSDK(state) {
            Object.assign(state, sdkInitialState);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSDKSettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getSDKSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                state.auctionParams = {
                    partner_id: payload.partner_id,
                    session_id: payload.session_id,
                    user: payload.user
                        ? {
                              balance: payload.user.balance,
                              balance_currency: 'USD',
                              email: payload.user.email,
                              firstname: payload.user.firstname,
                              lastname: payload.user.lastname || '',
                          }
                        : undefined,
                };
            })
            .addCase(getSDKSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions
export const { setAuctionParams, resetAuctionParams, setSDKLaunching, setSDKError, resetSDK } = sdkSlice.actions;

// Selectors
type RootState = { sdk: SDKState };

export const selectSDK = (state: RootState): SDKState => state.sdk;
export const selectAuctionParams = createSelector(selectSDK, (sdk) => sdk.auctionParams);
export const selectSDKLoading = createSelector(selectSDK, (sdk) => sdk.isLoading);
export const selectSDKLaunching = createSelector(selectSDK, (sdk) => sdk.isLaunching);
export const selectSDKError = createSelector(selectSDK, (sdk) => sdk.error);

export default sdkSlice.reducer;
